import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { installMissLogger } from '~/lib/i18n/miss-logger';
import { initSentry } from '~/lib/sentry';
import { AppProviders } from './providers';
import { router } from './router';

/**
 * F26 [Х39] — отметка сборки на самом документе.
 *
 * Кадр дизайн-гейта обязан доказывать, что снят С ЭТОЙ сборки: `mtime` файла
 * говорит лишь «после снимка не правили». Штамп проставляется на `<html>` и
 * виден в DOM кадра.
 */
declare const __BUILD_STAMP__: string;
document.documentElement.dataset['build'] = __BUILD_STAMP__;

/**
 * Видимая метка — ТОЛЬКО в dev и только по явному запросу (`?build_stamp=1`).
 *
 * Постоянная плашка в углу была бы дефектом сама по себе: дизайн-ревью судит
 * кадр целиком, и служебный элемент в нём — такой же шум, как любой другой.
 * Поэтому метка появляется на время съёмки кадров гейта и исчезает вместе с
 * параметром.
 */
if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('build_stamp')) {
  const el = document.createElement('div');
  el.textContent = `build ${__BUILD_STAMP__}`;
  el.setAttribute('data-testid', 'build-stamp');
  el.style.cssText =
    'position:fixed;left:4px;bottom:2px;z-index:9999;font:10px/1 monospace;' +
    'color:#888;background:rgba(0,0,0,.35);padding:2px 4px;border-radius:3px;pointer-events:none';
  document.body.appendChild(el);
}

// F16 — Sentry init до первого render (ловим init-errors).
initSentry();
// F-A.26 — i18n missing-key → Sentry warning (rate-limited).
installMissLogger();

// Graceful recovery от «протухших» lazy-чанков: Vite бросает `vite:preloadError`,
// когда dynamic import() не может загрузить свой чанк (напр. хеши сменились при
// деплое, пока сессия открыта, или окно dist-swap self-build). Без обработчика
// приложение падает в error-boundary («Что-то пошло не так»). Перезагружаем один
// раз, чтобы подтянуть свежий бандл; guard по времени — не зациклиться, если чанк
// реально отсутствует (тогда показываем error-boundary, а не loop reload).
window.addEventListener('vite:preloadError', (event: Event) => {
  const KEY = 'vite-preload-reload-at';
  const last = Number(sessionStorage.getItem(KEY) ?? 0);
  const now = Date.now();
  if (now - last > 10_000) {
    sessionStorage.setItem(KEY, String(now));
    event.preventDefault();
    window.location.reload();
  }
});

// Design tokens — импортируются первыми, чтобы каскад токенов применился до компонентов.
import '~/design-tokens/reset.css';
import '~/design-tokens/tokens.css';
import '~/design-tokens/typography.css';
import '~/design-tokens/link-inline.css';
import '~/design-tokens/ctxmenu-source.css';

import { installMinusCopyHandler } from '~/lib/format/copy-sign';
import { useThemeStore } from '~/store/theme';

// Применяем тему до первого render (data-theme уже выставлен inline-скриптом в index.html,
// здесь инициализируем store + подписку на смену system-темы).
useThemeStore.getState().init();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

// F101 (С-I, NC-45): U+2212 из показа уходит в буфер обмена ASCII-минусом (Excel читает число).
installMinusCopyHandler();

createRoot(rootEl).render(
  <StrictMode>
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  </StrictMode>,
);
